import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { gql, useQuery } from 'urql'
import Button from '~/components/buttons/Button'
import Dialog from '~/components/widgets/Dialog'
import { ImpactQuestionsActions, useEventsTracker, useFeatureToggle, useImpactQuestions } from '~/contexts'
import { createSubmission } from '~/libs/survey'
import { CheckSubmissionExistsOutput, Question } from '~/shared/api-generated-types'
import * as styles from './styles'

const CHECK_SUBMISSION_EXISTS = gql`
  query checkSubmissionExists($input: CheckSubmissionExistsInput!) {
    checkSubmissionExists(input: $input) {
      __typename
      exists
    }
  }
`

// Unified "degree" scale for both PRE and POST impact questions.
const DEGREE_SCALE = ['Not at all', 'A little', 'Somewhat', 'Quite a bit', 'Very much']
const ratingsTextMap = {
  preClass: DEGREE_SCALE,
  postClass: DEGREE_SCALE,
}

export type QuestionType = 'PRECLASS' | 'POSTCLASS'

interface Props {
  classId: string
  questionType: QuestionType
  question: Question
  sessionId?: string
  onClose: () => void
}

export default function ImpactQuestionsDialog(props: Props): JSX.Element {
  const { t } = useTranslation('lobby')
  const impactQuestionsEnabled = useFeatureToggle('exp_impact_questions')
  const { trackSurveyImpressionEvent, trackSurveySkipEvent, trackSurveySubmitEvent } = useEventsTracker()
  const { dispatch: setImpactQuestionsState } = useImpactQuestions()

  const [loading, setLoading] = useState(false)
  const [alreadySubmitted, setAlreadySubmitted] = useState(false)
  const [userRating, setUserRating] = useState(3)

  // If the question is not recurring, check if user has already submitted the feedback.
  const [{ data, fetching }] = useQuery<{
    checkSubmissionExists: CheckSubmissionExistsOutput
  }>({
    query: CHECK_SUBMISSION_EXISTS,
    variables: {
      input: {
        classId: props.classId,
        questionId: props.question.id,
      },
    },
    requestPolicy: 'cache-first',
    pause: !!props.question.recurring,
  })

  let scaleLabels
  switch (props.questionType) {
    case 'PRECLASS':
      scaleLabels = { 1: ratingsTextMap.preClass[0], 5: ratingsTextMap.preClass[4] }
      break

    case 'POSTCLASS':
      scaleLabels = { 1: ratingsTextMap.postClass[0], 5: ratingsTextMap.postClass[4] }
      break
  }

  function handleOnClase(): void {
    if (loading) return
    setImpactQuestionsState(
      props.questionType === 'PRECLASS'
        ? ImpactQuestionsActions.SET_PRE_CLASS_QUESTIONS_SKIPPED
        : ImpactQuestionsActions.SET_POST_CLASS_QUESTIONS_SKIPPED,
    )
    trackSurveySkipEvent({
      classId: props.classId,
      questionId: props.question.id,
      questionType: props.questionType,
      sessionId: props.sessionId,
    })
    props.onClose()
  }

  async function handleOnSubmit(): Promise<void> {
    try {
      setLoading(true)
      const ratingText =
        props.questionType === 'PRECLASS'
          ? ratingsTextMap.preClass[userRating - 1]
          : ratingsTextMap.postClass[userRating - 1]

      // Save users interaction
      await createSubmission({
        classId: props.classId,
        sessionId: props.sessionId,
        questionId: props.question.id,
        isRecurring: props.question.recurring,
        userRating,
        ratingText,
      })

      // Set internal states
      setImpactQuestionsState(
        props.questionType === 'PRECLASS'
          ? ImpactQuestionsActions.SET_PRE_CLASS_QUESTIONS_SUBMITTED
          : ImpactQuestionsActions.SET_POST_CLASS_QUESTIONS_SUBMITTED,
      )

      // Track event
      trackSurveySubmitEvent({
        classId: props.classId,
        questionId: props.question.id,
        questionType: props.questionType,
        userRating,
        sessionId: props.sessionId,
      })

      toast.success('Feedback submitted!', { duration: 5000 })
    } catch (error) {
      console.error('ImpactQuestionsDialogError', error)
    } finally {
      setLoading(false)
      props.onClose()
    }
  }

  useEffect(() => {
    if (!!props.question.recurring || fetching) return

    if (data && data.checkSubmissionExists && data.checkSubmissionExists.exists) {
      setAlreadySubmitted(true)
    }
  }, [data, fetching])

  useEffect(() => {
    if (!impactQuestionsEnabled || fetching || (!props.question.recurring && alreadySubmitted)) return

    // Set question asked
    setImpactQuestionsState(
      props.questionType === 'PRECLASS'
        ? ImpactQuestionsActions.SET_PRE_CLASS_QUESTIONS_ASKED
        : ImpactQuestionsActions.SET_POST_CLASS_QUESTIONS_ASKED,
    )

    // track render event
    trackSurveyImpressionEvent({
      classId: props.classId,
      sessionId: props.sessionId,
      questionId: props.question.id,
      questionType: props.questionType,
    })
  }, [impactQuestionsEnabled, fetching, alreadySubmitted])

  if (!impactQuestionsEnabled || fetching || (!props.question.recurring && alreadySubmitted)) {
    return <></>
  }
  return (
    <Dialog customClass={styles.customClass} onClose={handleOnClase}>
      <div className={styles.container}>
        <div className={styles.heading}>{props.question.questionText}</div>
        <br />
        <br />
        <div className={styles.optionsContainer}>
          {[1, 2, 3, 4, 5].map((rating) => (
            <label key={rating} className={styles.option} htmlFor={rating.toString()}>
              <input
                id={rating.toString()}
                type="radio"
                name="rating"
                value={rating}
                defaultChecked={rating === userRating}
                onChange={(event) => setUserRating(parseInt(event.target.value))}
              />
              {rating}
            </label>
          ))}
        </div>
        <div className={styles.optionsContainer}>
          {[1, 5].map((rating) => (
            <label
              key={rating}
              htmlFor={rating.toString()}
              className={styles.scale}
              style={{ textAlign: rating === 1 ? 'left' : 'right' }}
            >
              {scaleLabels[rating]}
            </label>
          ))}
        </div>
        <br />
        <br />
        <div className={styles.buttonContainer}>
          <Button secondaryAlt rounded small fullWidth loading={loading} onClick={handleOnSubmit}>
            {t('submitFeedback')}
          </Button>
          <Button primary rounded small fullWidth disabled={loading} onClick={handleOnClase}>
            {t('skip')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
